import React from 'react';

import { Icon } from '@column/column-ui-kit';

import { TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { ROUTE } from '~/app/routes';
import { TransferParty, TransferPartyAmount, TransferPartyDetail } from '~/components/ObjectCards/TransferParty';
import { TimelineEventStatus, TimelineEventsEntry } from '~/components/TimelineEvents';
import { Loan, LoanPayment } from '~/repositories';
import { BankAccount } from '~/repositories/BankAccountRepository';

const buildTransferPartyFromBankAccount = (
  bankAccount: BankAccount,
  accountNumberId: string | undefined,
  amount: TransferPartyAmount | undefined,
  entityType: 'person' | 'business' | undefined,
  status: TimelineStatus | undefined,
  navigate: (path: string) => void
): React.ReactNode => {
  const details: TransferPartyDetail[] = [];
  if (bankAccount.id) {
    details.push({ label: 'Bank Account ID', value: bankAccount.id });
  }
  const routingNumber =
    (bankAccount.defaultAccountNumber as any)?.routingNumber || bankAccount.routingNumber || undefined;
  if (routingNumber) {
    details.push({ label: 'Routing Number', value: routingNumber });
  }
  if (accountNumberId) {
    const accountNumber =
      accountNumberId === bankAccount.defaultAccountNumberId
        ? typeof bankAccount.defaultAccountNumber === 'string'
          ? bankAccount.defaultAccountNumber
          : (bankAccount.defaultAccountNumber as any)?.accountNumber
        : undefined;
    if (accountNumber) {
      details.push({ label: 'Account Number', value: accountNumber });
    } else {
      details.push({ label: 'Account Number ID', value: accountNumberId });
    }
  }

  const icon = entityType === 'person' ? <Icon.User /> : entityType === 'business' ? <Icon.Business /> : <Icon.Bank />;

  return (
    <TransferParty
      icon={icon}
      title={bankAccount.displayName || bankAccount.description || 'Unnamed Bank Account'}
      amount={amount}
      details={details.length > 0 ? details : undefined}
      onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount.id}`)}
      status={status}
    />
  );
};

const buildTransferPartyFromLoan = (
  loan: Loan,
  amount: TransferPartyAmount | undefined,
  status: TimelineStatus | undefined,
  navigate: (path: string) => void
): React.ReactNode => {
  const details: TransferPartyDetail[] = [];
  if (loan.id) {
    details.push({ label: 'Loan ID', value: loan.id });
  }

  return (
    <TransferParty
      icon={<Icon.AnimationHandCoins />}
      title={loan.description || 'Unnamed Loan'}
      amount={amount}
      details={details.length > 0 ? details : undefined}
      onClick={() => navigate(`${ROUTE.LOANS}/edit/${loan.id}`)}
      status={status}
    />
  );
};

export const buildLoanPaymentTimelineEvents = ({
  payment,
  loan,
  bankAccount,
  entityType,
  navigate,
}: {
  payment: LoanPayment;
  loan?: Loan;
  bankAccount?: BankAccount;
  entityType?: 'person' | 'business';
  navigate: (path: string) => void;
}): TimelineEventsEntry[] => {
  const status = payment.status?.toLowerCase();
  const completed = status === 'completed';
  const returned = status === 'returned';
  const hold = status === 'hold';

  const events: TimelineEventsEntry[] = [];

  if (payment.createdAt) {
    const amount: TransferPartyAmount | undefined = payment.principalAmount
      ? {
          value:
            typeof payment.principalAmount === 'string' ? parseFloat(payment.principalAmount) : payment.principalAmount,
          currencyCode: payment.currency || 'USD',
          isAdding: false,
          partyType: 'sender',
        }
      : undefined;

    const content = bankAccount
      ? buildTransferPartyFromBankAccount(
          bankAccount,
          payment.accountNumberId || undefined,
          amount,
          entityType,
          TimelineStatus.Success,
          navigate
        )
      : undefined;

    events.push({
      label: 'Created',
      date: new Date(payment.createdAt),
      status: TimelineEventStatus.Success,
      content,
    });
  }

  if (hold) {
    events.push({
      label: 'Hold',
      status: TimelineEventStatus.Hold,
      iconColor: (theme: any) => theme.warning.background,
    });
  }

  if (completed || hold) {
    const amount: TransferPartyAmount | undefined = payment.principalAmount
      ? {
          value:
            typeof payment.principalAmount === 'string' ? parseFloat(payment.principalAmount) : payment.principalAmount,
          currencyCode: payment.currency || 'USD',
          isAdding: true,
          partyType: 'receiver',
        }
      : undefined;

    const timelineStatus = hold ? TimelineStatus.InProgress : TimelineStatus.Success;
    const content = loan ? buildTransferPartyFromLoan(loan, amount, timelineStatus, navigate) : undefined;

    events.push({
      label: 'Completed',
      date: hold ? undefined : payment.effectiveAt ? new Date(payment.effectiveAt) : undefined,
      status: hold ? TimelineEventStatus.InProgress : TimelineEventStatus.Success,
      content,
    });
  }

  if (returned) {
    const amount: TransferPartyAmount | undefined = payment.principalAmount
      ? {
          value:
            typeof payment.principalAmount === 'string'
              ? parseFloat(String(payment.principalAmount))
              : payment.principalAmount,
          currencyCode: payment.currency || 'USD',
          isAdding: false,
          partyType: 'receiver',
        }
      : undefined;

    const content = loan ? buildTransferPartyFromLoan(loan, amount, TimelineStatus.Danger, navigate) : undefined;

    events.push({
      label: 'Returned',
      date: payment.effectiveAt ? new Date(payment.effectiveAt) : undefined,
      status: TimelineEventStatus.Danger,
      content,
    });
  }

  return events;
};
